import { Download, Search, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";

const steps = [
  {
    icon: Download,
    step: "Step 1",
    title: "Get Your Kit",
    description: "Download templates, guides, and tracking tools instantly after purchase.",
  },
  {
    icon: Search,
    step: "Step 2",
    title: "Identify Errors",
    description: "Use our checklist to find disputable items on your credit reports.",
  },
  {
    icon: Send,
    step: "Step 3",
    title: "Send Disputes",
    description: "Follow the step-by-step process, mail your letters, and track results.",
  },
];

const HowItWorks = () => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.05 });

  const scrollToKit = () => {
    document.querySelector("#kit-section")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="how-it-works" className="py-12 lg:py-16 bg-background overflow-hidden">
      <div className="container-custom" ref={ref}>
        <div className={cn(
          "text-center mb-10 transition-all duration-700",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        )}>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            How It <span className="text-gradient">Works</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Three simple steps to start disputing credit report errors.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto mb-10">
          {/* Connecting line SVG - hidden on mobile */}
          <svg
            className={cn(
              "absolute top-10 left-0 w-full h-1 hidden md:block",
              isVisible && "draw-line visible"
            )}
            style={{ zIndex: 0 }}
          >
            <line
              x1="16.67%"
              y1="50%"
              x2="83.33%"
              y2="50%"
              stroke="hsl(var(--primary))"
              strokeWidth="2"
              strokeDasharray="8 4"
              className={cn(
                "transition-all duration-1000",
                isVisible ? "opacity-100" : "opacity-0"
              )}
            />
          </svg>

          <div className="grid md:grid-cols-3 gap-8 relative z-10">
            {steps.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className={cn(
                    "text-center transition-all duration-700",
                    isVisible
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-8"
                  )}
                  style={{
                    transitionDelay: isVisible ? `${index * 200}ms` : "0ms",
                  }}
                >
                  <div className="relative inline-flex mb-6 group">
                    <div className={cn(
                      "w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center transition-all duration-300 group-hover:bg-primary/20 group-hover:scale-105",
                      isVisible && index === 0 && "animate-bounce-subtle"
                    )}>
                      <Icon className="w-10 h-10 text-primary" />
                    </div>
                    <span
                      className={cn(
                        "absolute -top-2 -right-2 w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center transition-all duration-500",
                        isVisible
                          ? "opacity-100 scale-100"
                          : "opacity-0 scale-0"
                      )}
                      style={{
                        transitionDelay: isVisible ? `${300 + index * 200}ms` : "0ms",
                      }}
                    >
                      {index + 1}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className={cn(
          "text-center transition-all duration-700 delay-500",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        )}>
          <Button variant="gold" size="lg" onClick={scrollToKit} className="group">
            Get Started with the DIY Kit
          </Button>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
